import { faTimes, faTrash } from '@fortawesome/free-solid-svg-icons'
import { useMemo, useState } from 'react'
import styled from 'styled-components'

import { Button } from '@/components'
import { Modal } from '@/components/Modal/Modal'
import { getFullySelectedNodes } from '@/components/TreeV2'
import { StructureObjectDto } from '@/endpoints/models'
import { useAppContext, useAppDispatch, useAppStore } from '@/hooks'
import { deleteNode, deselectNodes } from '@/store/modules/node/actions'
import { logError } from '@/utils/errorHandling'

import { useTreePanelOperations } from '../TreePanelContext'

type Props = {
	onClose: (removed: boolean) => void
}

export const DeleteNodesModal = ({ onClose }: Props) => {
	const { t } = useAppContext()
	const dispatch = useAppDispatch()
	const [loading, setLoading] = useState(false)
	const selectedNodes = useAppStore((state) => state.node.selectedNodes)
	const { nodes, children } = useAppStore((state) => state.node)
	const { refreshSystemsSilent } = useTreePanelOperations()

	// Filter out partially selected folders (indeterminate state)
	const fullySelectedNodes = useMemo(
		() => getFullySelectedNodes(selectedNodes, children, nodes),
		[selectedNodes, children, nodes],
	)

	const handleYesClick = async () => {
		setLoading(true)

		// delete only parent nodes (children are deleted automatically)
		const nodesParents = fullySelectedNodes.filter((nodeId) => {
			if (
				fullySelectedNodes.some(
					(selectedId) => children[selectedId]?.includes(nodeId),
				)
			) {
				return false
			}

			return true
		})

		const deleteRequests = nodesParents.map((nodeId) =>
			dispatch(deleteNode(nodes[nodeId] as StructureObjectDto)),
		)

		dispatch(deselectNodes(selectedNodes))
		await Promise.allSettled(deleteRequests)

		refreshSystemsSilent().catch((e) =>
			logError(e, { operation: 'DeleteNodesModal.refreshSystemsSilent' }),
		)

		setLoading(false)
		onClose(true)
	}

	return (
		<Modal
			open={true}
			contentStyle={{
				width: '400px',
			}}
			header={t('DELETE_SELECTED_ELEMENTS')}
			footer={(close) => (
				<>
					<Button
						icon={faTrash}
						schema="danger"
						onClick={handleYesClick}
						isLoading={loading}
					>
						{t('DELETE')}
					</Button>
					<Button schema="transparent" onClick={close} icon={faTimes}>
						{t('NO')}
					</Button>
				</>
			)}
			onClose={() => onClose(false)}
		>
			<PopupText>
				<span>
					{t('NUMBER_OF_SELECTED_ELEMENTS')}: {fullySelectedNodes.length}
				</span>
			</PopupText>
		</Modal>
	)
}

const PopupText = styled.div`
	display: flex;
	align-items: center;
`
